/**
 * FilesColumn — colonne verticale listant tous les fichiers (onglets) ouverts.
 *
 * S'insère entre la sidebar gauche (calques) et le canvas. Permet de basculer
 * d'un fichier à l'autre (utile après un import par lot : N PDF → N onglets).
 * Source de vérité : useTabsStore (mêmes données que la TabBar du haut).
 */
import React from 'react';
import { useTabsStore } from '../store/useTabsStore';

export default function FilesColumn({ onClose }) {
    const tabs = useTabsStore(s => s.tabs);
    const activeTabId = useTabsStore(s => s.activeTabId);
    const switchTab = useTabsStore(s => s.switchTab);
    const closeTab = useTabsStore(s => s.closeTab);

    return (
        <div className="w-56 shrink-0 bg-black/30 backdrop-blur-xl border-r border-white/5 flex flex-col z-[40] shadow-2xl relative">
            {/* Header */}
            <div className="px-3 py-2.5 border-b border-white/5 bg-black/20 flex items-center justify-between shrink-0">
                <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-white/60">
                    Fichiers ouverts <span className="text-white/30">({tabs.length})</span>
                </span>
                <button
                    onClick={onClose}
                    title="Masquer la liste des fichiers" aria-label="Masquer la liste des fichiers"
                    className="w-5 h-5 rounded flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-colors"
                >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
            </div>

            {/* Liste des fichiers */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-2 flex flex-col gap-1">
                {tabs.map((t) => {
                    const isActive = t.id === activeTabId;
                    return (
                        <div
                            key={t.id}
                            onClick={() => switchTab(t.id)}
                            title={t.name}
                            className={`group flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-all border ${
                                isActive
                                    ? 'bg-forge-accent/20 border-forge-accent/40 shadow-inner'
                                    : 'border-transparent hover:bg-white/5'
                            }`}
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={isActive ? 'text-forge-accent shrink-0' : 'text-white/40 shrink-0'}>
                                <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" />
                            </svg>
                            <span className={`flex-1 text-[11px] font-mono truncate ${isActive ? 'text-white' : 'text-white/70'}`}>
                                {t.name}
                            </span>
                            {t.isDirty && (
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0 shadow-[0_0_4px_rgba(251,191,36,0.6)]" title="Modifications non sauvegardées" />
                            )}
                            <button
                                onClick={(e) => { e.stopPropagation(); closeTab(t.id); }}
                                title="Fermer le fichier" aria-label="Fermer le fichier"
                                className="w-4 h-4 rounded flex items-center justify-center text-white/30 hover:text-forge-rose hover:bg-forge-rose/10 transition-all opacity-0 group-hover:opacity-100 shrink-0"
                            >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
